import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { AuthTokens, LoginInput, UserRole } from "@clinica/shared-types";
import { apiFetch } from "./http-client.js";
import { decodeAccessTokenClaims } from "./jwt.js";
import { getStoredTokens, setStoredTokens } from "./token-storage.js";

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  role: UserRole | null;
  isAdmin: boolean;
  userId: string | null;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
};

type Claims = { role: UserRole | null; alsoAdmin: boolean; userId: string | null };

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function claimsFromStorage(): Claims {
  const stored = getStoredTokens();
  const decoded = stored ? decodeAccessTokenClaims(stored.accessToken) : null;
  return { role: decoded?.role ?? null, alsoAdmin: decoded?.alsoAdmin ?? false, userId: decoded?.sub ?? null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => getStoredTokens() !== null);
  const [isLoading, setIsLoading] = useState(false);
  const [claims, setClaims] = useState<Claims>(claimsFromStorage);

  async function login(input: LoginInput) {
    setIsLoading(true);
    try {
      const tokens = await apiFetch<AuthTokens>("/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      });
      setStoredTokens(tokens);
      setIsAuthenticated(true);
      const decoded = decodeAccessTokenClaims(tokens.accessToken);
      setClaims({ role: decoded?.role ?? null, alsoAdmin: decoded?.alsoAdmin ?? false, userId: decoded?.sub ?? null });
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    const stored = getStoredTokens();
    setStoredTokens(null);
    setIsAuthenticated(false);
    setClaims({ role: null, alsoAdmin: false, userId: null });
    if (stored) {
      await apiFetch("/auth/logout", {
        method: "POST",
        body: JSON.stringify({ refreshToken: stored.refreshToken }),
      }).catch(() => {
        // logout é best-effort no servidor — a sessão local já foi limpa.
      });
    }
  }

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      role: claims.role,
      isAdmin: claims.role === "ADMIN" || claims.alsoAdmin,
      userId: claims.userId,
      login,
      logout,
    }),
    [isAuthenticated, isLoading, claims],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
